import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './Checkout.css';

const Checkout = () => {
  const { cart, getTotals, clearCart } = useCart();
  const navigate = useNavigate();
  const totals = getTotals();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
    deliveryDate: '',
    deliveryTime: '',
    specialInstructions: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }
    if (!formData.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
    if (!formData.deliveryTime) newErrors.deliveryTime = 'Delivery time is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Create order object
    const orderData = {
      items: cart.items,
      totals: totals,
      customerInfo: formData,
      orderDate: new Date().toISOString()
    };

    // Initialize Razorpay payment
    const options = {
      key: 'rzp_live_RkowgXNoCHe20H', // Replace with your Razorpay key_id
      amount: Math.round(totals.total * 100), // Amount in paise (multiply by 100)
      currency: 'INR',
      name: 'Woocoders',
      description: 'Cake Order Payment',
      image: 'https://supplebakes.com/wp-content/uploads/2023/01/supple_bakes_cropped_logo.png', // Your logo URL
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      theme: {
        color: '#9c6b5f' // Your primary color
      },
      handler: function (response) {
        // Payment successful
        console.log('Payment successful:', response);
        console.log('Order placed:', orderData);
        
        // Show success toast or alert
        if (window.plugins && window.plugins.toast) {
          window.plugins.toast.showLongCenter('🎉 Payment successful! Order placed.');
        } else {
          alert('🎉 Payment successful! Order placed. We will contact you soon.');
        }
        
        // Clear cart
        clearCart();
        
        // Redirect to home
        navigate('/');
      },
      modal: {
        ondismiss: function() {
          console.log('Payment cancelled by user');
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  if (cart.items.length === 0) {
    navigate('/');
    return null;
  }

  // Get minimum date (today)
  const today = new Date();
  const minDate = today.toISOString().split('T')[0];

  // Check if selected date is today
  const isToday = formData.deliveryDate === minDate;
  
  // Get current hour to determine available time slots
  const currentHour = new Date().getHours();
  
  // Determine which time slots are available
  const getAvailableTimeSlots = () => {
    if (!isToday) {
      // All slots available for future dates
      return {
        morning: true,
        afternoon: true,
        evening: true
      };
    }
    
    // For today, disable past time slots
    return {
      morning: currentHour < 12,      // Available if before 12 PM
      afternoon: currentHour < 16,    // Available if before 4 PM
      evening: currentHour < 20       // Available if before 8 PM
    };
  };
  
  const availableSlots = getAvailableTimeSlots();

  return (
    <div className="checkout-page">
      <header className="checkout-header glass-effect">
        <div className="checkout-header-content">
          <Link to="/cart" className="back-button ripple">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </Link>
          <h1 className="checkout-title">Checkout</h1>
        </div>
      </header>

      <div className="checkout-content">
        <div className="container">
          <div className="checkout-layout">
            {/* Checkout Form */}
            <div className="checkout-form-section">
              <form onSubmit={handleSubmit} className="checkout-form">
                <div className="form-section">
                  <h2 className="section-heading">
                    <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    Contact Information
                  </h2>

                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`form-input ${errors.name ? 'error' : ''}`}
                        placeholder="Enter your full name"
                      />
                      {errors.name && <span className="error-message">{errors.name}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`form-input ${errors.email ? 'error' : ''}`}
                        placeholder="your@email.com"
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`form-input ${errors.phone ? 'error' : ''}`}
                        placeholder="10-digit mobile number"
                      />
                      {errors.phone && <span className="error-message">{errors.phone}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h2 className="section-heading">
                    <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    Delivery Address
                  </h2>

                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label className="form-label">Street Address *</label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className={`form-textarea ${errors.address ? 'error' : ''}`}
                        placeholder="House/Flat No., Street Name, Area"
                        rows="3"
                      ></textarea>
                      {errors.address && <span className="error-message">{errors.address}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`form-input ${errors.city ? 'error' : ''}`}
                        placeholder="City name"
                      />
                      {errors.city && <span className="error-message">{errors.city}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Pincode *</label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        className={`form-input ${errors.pincode ? 'error' : ''}`}
                        placeholder="6-digit pincode"
                        maxLength="6"
                      />
                      {errors.pincode && <span className="error-message">{errors.pincode}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h2 className="section-heading">
                    <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    Delivery Schedule
                  </h2>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Delivery Date *</label>
                      <input
                        type="date"
                        name="deliveryDate"
                        value={formData.deliveryDate}
                        onChange={handleChange}
                        className={`form-input ${errors.deliveryDate ? 'error' : ''}`}
                        min={minDate}
                      />
                      {errors.deliveryDate && <span className="error-message">{errors.deliveryDate}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Delivery Time *</label>
                      <select
                        name="deliveryTime"
                        value={formData.deliveryTime}
                        onChange={handleChange}
                        className={`form-select ${errors.deliveryTime ? 'error' : ''}`}
                      >
                        <option value="">Select time slot</option>
                        <option value="morning" disabled={!availableSlots.morning}>
                          Morning (9 AM - 12 PM) {!availableSlots.morning && '(Not available)'}
                        </option>
                        <option value="afternoon" disabled={!availableSlots.afternoon}>
                          Afternoon (12 PM - 4 PM) {!availableSlots.afternoon && '(Not available)'}
                        </option>
                        <option value="evening" disabled={!availableSlots.evening}>
                          Evening (4 PM - 8 PM) {!availableSlots.evening && '(Not available)'}
                        </option>
                      </select>
                      {errors.deliveryTime && <span className="error-message">{errors.deliveryTime}</span>}
                    </div>

                    <div className="form-group full-width">
                      <label className="form-label">Special Instructions (Optional)</label>
                      <textarea
                        name="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={handleChange}
                        className="form-textarea"
                        placeholder="Any special requests or instructions for delivery"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <button type="submit" className="submit-btn btn-primary ripple">
                  Place Order - ₹{totals.total.toFixed(2)}
                </button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="order-summary-sidebar">
              <div className="order-summary card">
                <h2 className="summary-title">Order Summary</h2>
                
                <div className="summary-items">
                  {cart.items.map(item => (
                    <div key={item.id} className="summary-item">
                      <div className="summary-item-details">
                        <div className="summary-item-name">{item.cakeTitle}</div>
                        <div className="summary-item-meta">
                          {item.weight}kg • {item.flavourName} • Qty: {item.quantity}
                        </div>
                      </div>
                      <div className="summary-item-price">₹{item.subtotal.toLocaleString()}</div>
                    </div>
                  ))}
                </div>

                <div className="summary-divider"></div>

                <div className="summary-rows">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>₹{totals.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="summary-row">
                    <span>Tax (5%)</span>
                    <span>₹{totals.tax.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Delivery Fee</span>
                    <span>₹{totals.deliveryFee}</span>
                  </div>
                  <div className="summary-total">
                    <span>Total</span>
                    <span className="total-value">₹{totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
